import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { Item } from '@/features/backlog/types';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { useWatches } from '@/features/backlog/watches';
import { getPushState, enablePush, disablePush, PushState } from '@/features/push/push';
import { boardPath, taskPath } from '@/constants/routes';
import { Eye, Moon, Sun, TaskTypeGlyph } from '@/components/ui/icons';
import { taskTypeMeta } from '@/features/backlog/constants';
import { useTheme } from '@/components/theme-toggle';
import MemberAvatar from '@/components/member-avatar';
import DesktopUpdatePanel from '@/features/identity/desktop-update-panel';
import DesktopNotifyPanel from '@/features/identity/desktop-notify-panel';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import { cn } from '@/components/ui/cn';

/** A monitor glyph for the "System" (follow-OS) theme option. */
const MonitorGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

/** User settings: email-notification preference + the watch list (items & boards you follow, which
 *  feed the hourly email digest). */
export default function SettingsPage() {
  const { me } = useIdentity();
  const { boards } = useAppData();
  const { pref, setPref } = useTheme();
  const wc = useWatches();
  const navigate = useNavigate();
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [emailOn, setEmailOn] = useState(true);
  const [items, setItems] = useState<Map<string, Item>>(new Map());
  const [pushState, setPushState] = useState<PushState>('off');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  useEffect(() => {
    api.getMyPreferences().then((p) => setEmailOn(p.email_notifications !== false)).catch(() => undefined);
    getPushState().then(setPushState).catch(() => undefined);
  }, []);

  // Enabling/disabling push is per-browser (permission + subscription live in the browser); the server
  // just stores this browser's subscription so it can be delivered to.
  const togglePush = async () => {
    if (pushBusy || pushState === 'unsupported' || pushState === 'unavailable' || pushState === 'denied') return;
    setPushBusy(true);
    setPushError('');
    try {
      setPushState(pushState === 'on' ? await disablePush() : await enablePush());
    } catch {
      // Subscribe/permission can reject (push service unreachable, permission dismissed, stale sub) —
      // surface it instead of silently snapping back, and re-read the real state.
      setPushError('Couldn’t update push notifications. Check this site’s notification permission in your browser and try again.');
      setPushState(await getPushState().catch(() => 'off' as PushState));
    } finally {
      setPushBusy(false);
    }
  };

  // Resolve watched items to their key/title for the list.
  const watchedItemIds = useMemo(
    () => (wc?.watches ?? []).filter((w) => w.target_type === 'item').map((w) => w.target_id),
    [wc?.watches],
  );
  useEffect(() => {
    watchedItemIds.forEach((id) => {
      if (items.has(id)) return;
      api.getItem(id).then((it) => setItems((m) => new Map(m).set(id, it))).catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedItemIds.join(',')]);

  const setEmail = (v: boolean) => {
    setEmailOn(v);
    api.saveMyPreferences({ email_notifications: v }).catch(() => undefined);
  };

  const watchedBoards = (wc?.watches ?? []).filter((w) => w.target_type === 'board');
  const watchedItems = (wc?.watches ?? []).filter((w) => w.target_type === 'item');

  return (
    <div className="mx-auto w-full max-w-[760px]">
      <div className="mb-6 flex items-center gap-3">
        {me ? <MemberAvatar member={me} size={44} /> : null}
        <div>
          <h1 className="text-[20px] font-semibold text-fg-strong">Settings</h1>
          <p className="text-[13px] text-fg-faint">{me?.email}</p>
        </div>
      </div>

      {/* Desktop app updates (only inside the desktop shell; renders null in a browser) */}
      <DesktopUpdatePanel variant="section" />

      {/* Appearance / theme */}
      <section className="mb-6 rounded-2xl border border-line bg-surface-raised p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[14px] font-semibold text-fg-strong">Appearance</div>
            <p className="mt-0.5 text-[12.5px] text-fg-faint">Follow your system, or force light/dark. Saved to your profile, so it follows you across devices.</p>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-full bg-layer p-1">
            {(['system', 'light', 'dark'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setPref(t)}
                aria-pressed={pref === t}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
                  pref === t ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-faint hover:text-fg-soft',
                )}
              >
                {t === 'system' ? <MonitorGlyph className="h-4 w-4" /> : t === 'light' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {t === 'system' ? 'System' : t === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Email notifications */}
      <section className="mb-6 rounded-2xl border border-line bg-surface-raised p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[14px] font-semibold text-fg-strong">Email digests</div>
            <p className="mt-0.5 text-[12.5px] text-fg-faint">An hourly summary of activity on the items and boards you watch — only when there’s something new.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={emailOn}
            onClick={() => setEmail(!emailOn)}
            className={cn('relative h-6 w-11 shrink-0 rounded-full transition-colors', emailOn ? 'bg-accent' : 'bg-layer-strong')}
          >
            <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', emailOn ? 'left-[22px]' : 'left-0.5')} />
          </button>
        </div>
      </section>

      {/* Desktop app notifications (native — shown only inside the desktop shell) */}
      <DesktopNotifyPanel />

      {/* Web push notifications — browser only; a WebView (desktop app) has no Push API, so it's hidden
          there in favour of the native Desktop notifications above. */}
      {isDesktopApp() ? null : (
      <section className="mb-6 rounded-2xl border border-line bg-surface-raised p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[14px] font-semibold text-fg-strong">Web push notifications</div>
            <p className="mt-0.5 text-[12.5px] text-fg-faint">
              {pushState === 'unsupported'
                ? 'This browser doesn’t support push notifications.'
                : pushState === 'unavailable'
                  ? 'Push notifications aren’t configured on this server yet.'
                  : pushState === 'denied'
                    ? 'Notifications are blocked for this site — re-enable them in your browser settings, then turn this on.'
                    : 'A desktop notification the moment someone @mentions you — even when Spark Board isn’t open. Enabled per browser.'}
            </p>
            {pushError ? <p className="mt-1.5 text-[12px] text-danger">{pushError}</p> : null}
          </div>
          {pushState === 'unsupported' || pushState === 'unavailable' ? null : (
            <button
              type="button"
              role="switch"
              aria-checked={pushState === 'on'}
              disabled={pushBusy || pushState === 'denied'}
              onClick={togglePush}
              className={cn('relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50', pushState === 'on' ? 'bg-accent' : 'bg-layer-strong')}
            >
              <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', pushState === 'on' ? 'left-[22px]' : 'left-0.5')} />
            </button>
          )}
        </div>
      </section>
      )}

      {/* Watch list */}
      <section className="rounded-2xl border border-line bg-surface-raised p-5">
        <div className="mb-3 flex items-center gap-2">
          <Eye className="text-fg-faint" />
          <h2 className="text-[14px] font-semibold text-fg-strong">Watching</h2>
        </div>

        {watchedBoards.length === 0 && watchedItems.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-fg-faint">
            You’re not watching anything yet. Open an item or board and hit <span className="text-fg-muted">Watch</span> to get its updates.
          </p>
        ) : (
          <div className="space-y-4">
            {watchedBoards.length ? (
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Boards</div>
                <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line">
                  {watchedBoards.map((w) => {
                    const b = boardById.get(w.target_id);
                    return (
                      <li key={w.target_id} className="flex items-center gap-2 px-3 py-2">
                        <button type="button" onClick={() => b && navigate(boardPath(b.slug))} className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-fg hover:underline">
                          {b?.name ?? 'Board'}
                        </button>
                        <button type="button" onClick={() => wc?.toggle('board', w.target_id)} className="shrink-0 text-[12px] text-fg-faint transition-colors hover:text-danger">
                          Unwatch
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {watchedItems.length ? (
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Items</div>
                <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line">
                  {watchedItems.map((w) => {
                    const it = items.get(w.target_id);
                    const b = it ? boardById.get(it.board_id) : undefined;
                    return (
                      <li key={w.target_id} className="flex items-center gap-2.5 px-3 py-2">
                        {it ? <TaskTypeGlyph type={it.type} color={taskTypeMeta(it.type).color} className="shrink-0" /> : null}
                        <button
                          type="button"
                          onClick={() => it && b && navigate(taskPath(b.slug, it.key))}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          {it ? <span className="item-key shrink-0 rounded px-1 py-px tnum text-[11px] font-semibold">{it.key}</span> : null}
                          <span className="min-w-0 truncate text-[13px] text-fg hover:underline">{it?.title ?? 'Item'}</span>
                        </button>
                        <button type="button" onClick={() => wc?.toggle('item', w.target_id)} className="shrink-0 text-[12px] text-fg-faint transition-colors hover:text-danger">
                          Unwatch
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
